import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { 
  Baby, Settings, Shield, Globe, Package, PartyPopper,
  AlertTriangle, CheckCircle, RefreshCw, Edit2, Save,
  BarChart3, Users, ShoppingBag, Gamepad2
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Stats Card Component
function StatsCard({ title, value, icon: Icon, color, subtitle }) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-500">{title}</p>
            <p className={`text-2xl font-bold ${color}`}>{value}</p>
            {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
          </div>
          <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${color.replace('text-', 'bg-').replace('600', '100')}`}>
            <Icon className={`w-6 h-6 ${color}`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Country Rules Editor
function CountryRulesEditor({ country, rules, onSave }) {
  const [editing, setEditing] = useState(false);
  const [localRules, setLocalRules] = useState(rules || {});
  
  const handleSave = () => {
    onSave(country, localRules);
    setEditing(false);
  };
  
  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Globe className="w-5 h-5" />
            {country === 'UK' ? '🇬🇧 United Kingdom' : '🇱🇰 Sri Lanka'} Rules
          </CardTitle>
          <Button 
            variant={editing ? 'default' : 'outline'} 
            size="sm"
            onClick={() => editing ? handleSave() : setEditing(true)}
          >
            {editing ? <><Save className="w-4 h-4 mr-1" /> Save</> : <><Edit2 className="w-4 h-4 mr-1" /> Edit</>}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label>Strict Safety Mode</Label>
              <p className="text-sm text-gray-500">Require extra disclaimers for sensitive services</p>
            </div>
            <Switch 
              checked={localRules.strict_safety || false}
              disabled={!editing}
              onCheckedChange={(v) => setLocalRules({...localRules, strict_safety: v})}
            />
          </div>
          
          <div className="flex items-center justify-between">
            <div>
              <Label>Photo Consent Required</Label>
              <p className="text-sm text-gray-500">For photography services</p>
            </div>
            <Switch 
              checked={localRules.photo_consent_required || false}
              disabled={!editing}
              onCheckedChange={(v) => setLocalRules({...localRules, photo_consent_required: v})}
            />
          </div>
          
          <div className="flex items-center justify-between">
            <div>
              <Label>Age Suitability Required</Label>
              <p className="text-sm text-gray-500">Require age labels on toys</p>
            </div>
            <Switch 
              checked={localRules.age_suitability_required || false}
              disabled={!editing}
              onCheckedChange={(v) => setLocalRules({...localRules, age_suitability_required: v})}
            />
          </div>
          
          {country === 'LK' && (
            <div className="flex items-center justify-between">
              <div>
                <Label>Cash on Delivery</Label>
                <p className="text-sm text-gray-500">Allow COD payment option</p>
              </div>
              <Switch 
                checked={localRules.cash_on_delivery || false}
                disabled={!editing}
                onCheckedChange={(v) => setLocalRules({...localRules, cash_on_delivery: v})}
              />
            </div>
          )}
          
          <div className="pt-4 border-t">
            <Label>Verification Levels</Label>
            <div className="grid grid-cols-2 gap-2 mt-2">
              {['car_seats', 'prams_strollers', 'party_entertainers', 'default'].map(key => (
                <div key={key} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                  <span className="text-sm capitalize">{key.replace(/_/g, ' ')}</span>
                  <Select 
                    value={String(localRules.verification_levels?.[key] || 0)}
                    disabled={!editing}
                    onValueChange={(v) => setLocalRules({
                      ...localRules,
                      verification_levels: {
                        ...localRules.verification_levels,
                        [key]: parseInt(v)
                      }
                    })}
                  >
                    <SelectTrigger className="w-20 h-8">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0">L0</SelectItem>
                      <SelectItem value="1">L1</SelectItem>
                      <SelectItem value="2">L2</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Subcategory Toggle Row
function SubcategoryToggle({ category, country, onToggle }) {
  const [enabled, setEnabled] = useState(true);
  const [verificationLevel, setVerificationLevel] = useState(0);
  
  return (
    <TableRow>
      <TableCell>
        <div className="flex items-center gap-2">
          <span className="text-xl">{category.icon}</span>
          <div>
            <p className="font-medium">{category.name}</p>
            <p className="text-xs text-gray-500">{category.services_count} services</p>
          </div>
        </div>
      </TableCell>
      <TableCell>
        {category.sensitive && (
          <Badge variant="outline" className="bg-amber-50 text-amber-700">Safety-Aware</Badge>
        )}
        {category.subscription_available && (
          <Badge variant="outline" className="bg-purple-50 text-purple-700 ml-1">Subscription</Badge>
        )}
      </TableCell>
      <TableCell>
        <Switch 
          checked={enabled}
          onCheckedChange={(v) => {
            setEnabled(v);
            onToggle(category.id, { enabled: v, verificationLevel });
          }}
        />
      </TableCell>
      <TableCell>
        <Select 
          value={String(verificationLevel)}
          onValueChange={(v) => {
            setVerificationLevel(parseInt(v));
            onToggle(category.id, { enabled, verificationLevel: parseInt(v) });
          }}
        >
          <SelectTrigger className="w-24">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="0">L0 - Basic</SelectItem>
            <SelectItem value="1">L1 - ID</SelectItem>
            <SelectItem value="2">L2 - Enhanced</SelectItem>
          </SelectContent>
        </Select>
      </TableCell>
    </TableRow>
  );
}

// Main Admin Component
export default function AdminBabyKids() {
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCountry, setSelectedCountry] = useState('UK');
  const [countryRules, setCountryRules] = useState({});
  
  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/baby-kids/admin/stats`);
      const data = await response.json();
      
      if (data.success) {
        setStats(data.stats);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  }, []);
  
  // Fetch categories
  const fetchCategories = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/baby-kids/categories?country=${selectedCountry}`);
      const data = await response.json();
      
      if (data.success) {
        setCategories(data.categories || []);
      }
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  }, [selectedCountry]);
  
  // Fetch country rules
  const fetchCountryRules = useCallback(async () => {
    try {
      const [ukRes, lkRes] = await Promise.all([
        fetch(`${API_URL}/api/baby-kids/admin/country-rules/UK`),
        fetch(`${API_URL}/api/baby-kids/admin/country-rules/LK`),
      ]);
      
      const ukData = await ukRes.json();
      const lkData = await lkRes.json();
      
      setCountryRules({
        UK: ukData.rules || {},
        LK: lkData.rules || {},
      });
    } catch (error) {
      console.error('Failed to fetch country rules:', error);
    } finally {
      setLoading(false);
    }
  }, []);
  
  useEffect(() => {
    fetchStats();
    fetchCategories();
    fetchCountryRules();
  }, [fetchStats, fetchCategories, fetchCountryRules]);
  
  // Seed data
  const handleSeedData = async () => {
    try {
      const response = await fetch(`${API_URL}/api/baby-kids/seed`, { method: 'POST' });
      const data = await response.json();
      
      if (data.success) {
        toast.success(`Seeded ${data.listings_created} listings and ${data.providers_created} providers`);
        fetchStats();
      }
    } catch (error) {
      toast.error('Failed to seed data');
    }
  };
  
  // Toggle subcategory
  const handleToggleSubcategory = (categoryId, settings) => {
    toast.success(`Updated ${categoryId} settings`);
  };
  
  // Save country rules
  const handleSaveCountryRules = (country, rules) => {
    setCountryRules(prev => ({ ...prev, [country]: rules }));
    toast.success(`${country} rules updated`);
  };
  
  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Baby className="w-7 h-7 text-blue-600" />
              Baby & Kids Admin
            </h1>
            <p className="text-gray-500 mt-1">Manage categories, services, and country rules</p>
          </div>
          
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => { fetchStats(); fetchCategories(); }}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <Button onClick={handleSeedData}>
              <Package className="w-4 h-4 mr-2" />
              Seed Data
            </Button>
          </div>
        </div>
        
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
          <StatsCard 
            title="Total Listings" 
            value={stats?.total_listings || 0}
            icon={ShoppingBag}
            color="text-blue-600"
          />
          <StatsCard 
            title="Active Listings" 
            value={stats?.active_listings || 0}
            icon={CheckCircle}
            color="text-green-600"
          />
          <StatsCard 
            title="Providers" 
            value={stats?.total_providers || 0}
            icon={Users}
            color="text-purple-600"
          />
          <StatsCard 
            title="Total Bookings" 
            value={stats?.total_bookings || 0}
            icon={PartyPopper}
            color="text-pink-600"
          />
          <StatsCard 
            title="Pending Bookings" 
            value={stats?.pending_bookings || 0}
            icon={AlertTriangle}
            color="text-amber-600"
          />
        </div>
        
        {/* Main Content */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-4">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="categories">Categories</TabsTrigger>
            <TabsTrigger value="country-rules">Country Rules</TabsTrigger>
            <TabsTrigger value="safety">Safety & Wording</TabsTrigger>
          </TabsList>
          
          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="w-5 h-5" />
                    Listings by Category
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {stats?.by_subcategory?.length > 0 ? (
                    <div className="space-y-2">
                      {stats.by_subcategory.map(item => (
                        <div key={item.subcategory} className="flex items-center justify-between">
                          <span className="text-sm">{item.name}</span>
                          <Badge variant="secondary">{item.count}</Badge>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-4">No listings yet. Click &quot;Seed Data&quot; to add sample listings.</p>
                  )}
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Shield className="w-5 h-5" />
                    Safety Overview
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                      <span className="text-green-700">Blocked phrases active</span>
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    </div>
                    <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                      <span className="text-green-700">Disclaimers auto-applied</span>
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    </div>
                    <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg">
                      <span className="text-amber-700">Car seats: Safety-aware mode</span>
                      <Shield className="w-5 h-5 text-amber-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
          
          {/* Categories Tab */}
          <TabsContent value="categories">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Manage Subcategories</CardTitle>
                  <Select value={selectedCountry} onValueChange={setSelectedCountry}>
                    <SelectTrigger className="w-40">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="UK">🇬🇧 UK</SelectItem>
                      <SelectItem value="LK">🇱🇰 Sri Lanka</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <CardDescription>
                  Enable/disable subcategories and set verification levels per country
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Category</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Enabled</TableHead>
                      <TableHead>Verification Level</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {categories.map(cat => (
                      <SubcategoryToggle 
                        key={cat.id}
                        category={cat}
                        country={selectedCountry}
                        onToggle={handleToggleSubcategory}
                      />
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* Country Rules Tab */}
          <TabsContent value="country-rules">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <CountryRulesEditor 
                country="UK"
                rules={countryRules.UK}
                onSave={handleSaveCountryRules}
              />
              <CountryRulesEditor 
                country="LK"
                rules={countryRules.LK}
                onSave={handleSaveCountryRules}
              />
            </div>
          </TabsContent>
          
          {/* Safety Tab */}
          <TabsContent value="safety">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-green-700">
                    <CheckCircle className="w-5 h-5" />
                    Allowed Wording
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {['Cleaned', 'Sanitised', 'Age-appropriate', 'Parent-reviewed', 'Entertainment service', 'Child-friendly', 'Info-only', 'Guidance', 'Assistance'].map(word => (
                      <Badge key={word} variant="outline" className="bg-green-50 text-green-700 border-green-200">
                        ✓ {word}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-red-700">
                    <AlertTriangle className="w-5 h-5" />
                    Blocked Wording
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {['Sterilised', 'Medical-grade', 'Certified', 'Approved', 'Official', 'Licensed', 'Supervision', 'Childcare', 'Guaranteed safe'].map(word => (
                      <Badge key={word} variant="outline" className="bg-red-50 text-red-700 border-red-200">
                        ✗ {word}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
              
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle>Global Disclaimers</CardTitle>
                  <CardDescription>Auto-applied to relevant service types</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="font-medium text-amber-800 mb-1">Entertainment Services</p>
                    <p className="text-sm text-amber-700">&quot;This is an entertainment or venue booking service. It does not provide childcare or supervision.&quot;</p>
                  </div>
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="font-medium text-blue-800 mb-1">Hire Services</p>
                    <p className="text-sm text-blue-700">&quot;Items are cleaned between hires. This service does not provide childcare or developmental guarantees.&quot;</p>
                  </div>
                  <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg">
                    <p className="font-medium text-purple-800 mb-1">Safety-Aware Services</p>
                    <p className="text-sm text-purple-700">&quot;This is an information and guidance service only. It does not represent official certification or approval.&quot;</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
